import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import { api } from '@/api/v2/client'
import type { AiConversation } from '@/stores/ai'

// --- Typy ---
export interface AiProvider {
  id: string
  name: string
  models: { id: string; name: string }[]
  available: boolean
}

export interface AiProvidersResponse {
  providers: AiProvider[]
}

export interface AiConversationsResponse {
  conversations: AiConversation[]
}

// --- Query Keys ---
export const aiKeys = {
  all: ['ai'] as const,
  conversations: () => [...aiKeys.all, 'conversations'] as const,
  providers: () => [...aiKeys.all, 'providers'] as const,
}

// --- Query Hooky ---
export function useAiConversationList() {
  return useQuery({
    queryKey: aiKeys.conversations(),
    queryFn: () => api.get<AiConversationsResponse>('/ai/conversations'),
    refetchOnMount: 'always',
    staleTime: 0,
  })
}

export function useAiProviders() {
  return useQuery({
    queryKey: aiKeys.providers(),
    queryFn: () => api.get<AiProvidersResponse>('/ai/providers'),
  })
}

// --- Mutation Hooky ---
export function useRenameConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) =>
      api.patch(`/ai/conversations/${id}`, { title }),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<AiConversationsResponse>(aiKeys.conversations(), old => {
        if (!old) return old
        return {
          ...old,
          conversations: old.conversations.map(c =>
            c.id === variables.id ? { ...c, title: variables.title } : c,
          ),
        }
      })
    },
  })
}

export function useDeleteConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.delete(`/ai/conversations/${id}`),
    onSuccess: (_, variables) => {
      queryClient.setQueryData<AiConversationsResponse>(aiKeys.conversations(), old => {
        if (!old) return old
        return {
          ...old,
          conversations: old.conversations.filter(c => c.id !== variables.id),
        }
      })
    },
  })
}
